/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { LeaderboardPlayer } from '@sportninja/common/hooks/useLeaderboard'
import React from 'react'
import { Link } from 'react-router-dom'
import Text from 'src/components/ts-components/Text'

interface LeaderboardCardProps {
  title: string
  statId?: string
  abbreviation?: string
  players: LeaderboardPlayer[]
  precision?: number
  scheduleId?: string
  onViewMore?: () => void
  displayTeam?: boolean
}

/**
 * Format stat value with the specified precision and thousand separators
 * (e.g., 1265 → "1,265")
 */
const formatStatValue = (
  value: number | string,
  precision: number = 0
): string => {
  const numValue = typeof value === 'number' ? value : parseFloat(String(value))
  if (isNaN(numValue)) {
    return String(value)
  }
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
}

const LeaderboardCard: React.FC<LeaderboardCardProps> = ({
  title,
  statId,
  players,
  precision = 0,
  scheduleId,
  onViewMore,
  displayTeam = true,
}) => {
  if (!players || players.length === 0) {
    return null
  }

  const topPlayer = players[0]
  const otherPlayers = players.slice(1, players.length - 1)

  // Build team link with schedule query param
  const getTeamLink = (teamId: string) => {
    if (!teamId) {
      return '#'
    }
    const baseUrl = `/team/${teamId}`
    return scheduleId ? `${baseUrl}?sn_schedule=${scheduleId}` : baseUrl
  }

  // Build player link
  const getPlayerLink = (playerId: string) => {
    if (!playerId) {
      return '#'
    }
    return `${ROUTES.PLAYER_ROOT}/${playerId}`
  }

  return (
    <div
      css={css`
        min-height: 300px;
        width: 100%;
        border-radius: 8px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      `}
    >
      {/* Header */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          padding: 8px 0;
        `}
      >
        <Text
          variant='heading6'
          weight='semiBold'
          color={colors.NEUTRAL_0}
          style='text-transform: uppercase;'
        >
          {title}
        </Text>
        {onViewMore && statId && (
          <button
            type='button'
            onClick={onViewMore}
            css={css`
              background: none;
              border: none;
              padding: 0;
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 400;
              line-height: 17.4px;
              color: ${colors.PRIMARY};
              cursor: pointer;
              transition: opacity 0.2s ease;

              &:hover {
                opacity: 0.8;
              }
            `}
          >
            View More
          </button>
        )}
      </div>

      {/* Card Content */}
      <div
        css={css`
          border: 1px solid ${colors.SECONDARY_300};
          border-radius: 8px;
          overflow: hidden;
        `}
      >
        {/* Top Player Section */}
        <div
          css={css`
            background-color: ${colors.SECONDARY_300};
            padding: 16px;
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          {/* Rank */}
          <Text
            variant='paragraphMedium'
            weight='semiBold'
            color={colors.NEUTRAL_50}
            style='width: 18px; flex-shrink: 0; margin-right: -4px; margin: 0;'
          >
            #{topPlayer.rank}
          </Text>
          {/* Player Avatar - Clickable */}
          <Link
            to={getPlayerLink(topPlayer.playerId)}
            css={css`
              width: 56px;
              height: 56px;
              flex-shrink: 0;
              border-radius: 50%;
              overflow: hidden;
              background-color: ${colors.NEUTRAL_0};
              transition: opacity 0.2s ease;

              &:hover {
                opacity: 0.8;
              }
            `}
          >
            <img
              src={topPlayer.playerImage}
              alt={topPlayer.name}
              css={css`
                width: 100%;
                height: 100%;
                object-fit: cover;
              `}
            />
          </Link>

          {/* Player Info */}
          <div
            css={css`
              flex: 1;
              min-width: 0;
              display: flex;
              flex-direction: column;
              gap: 4px;
            `}
          >
            <Link
              to={getPlayerLink(topPlayer.playerId)}
              css={css`
                text-decoration: none;
                transition: opacity 0.2s ease;

                &:hover {
                  opacity: 0.8;
                }
              `}
            >
              <Text
                variant='heading6'
                weight='semiBold'
                color={colors.NEUTRAL_0}
                style='white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin: 0;'
              >
                {topPlayer.name}
              </Text>
            </Link>
            <Link
              to={getTeamLink(topPlayer.teamId)}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                text-decoration: none;
                transition: opacity 0.2s ease;

                &:hover {
                  opacity: 0.8;
                }
                ${displayTeam ? '' : 'display: none;'}
              `}
            >
              <div
                css={css`
                  width: 24px;
                  height: 24px;
                  flex-shrink: 0;
                  border-radius: 4px;
                  overflow: hidden;
                  background-color: ${colors.NEUTRAL_0};
                `}
              >
                <img
                  src={topPlayer.teamLogo}
                  alt={topPlayer.teamName}
                  css={css`
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                  `}
                />
              </div>
              <Text
                variant='paragraphXSmall'
                weight='medium'
                color={colors.NEUTRAL_50}
                style='white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin: 0;'
              >
                {topPlayer.teamName}
              </Text>
            </Link>
          </div>

          {/* Stat Value */}
          <Text
            variant='heading2'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style='flex-shrink: 0; margin: 0;'
          >
            {formatStatValue(topPlayer.statValue, precision)}
          </Text>
        </div>

        {/* Other Players List */}
        {otherPlayers.map((player) => (
          <div
            key={player.id}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 8px 16px;
            `}
          >
            {/* Rank */}
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
              style='width: 18px; flex-shrink: 0; margin: 0;'
            >
              #{player.rank}
            </Text>

            {/* Player Avatar - Clickable */}
            <Link
              to={getPlayerLink(player.playerId)}
              css={css`
                width: 32px;
                height: 32px;
                flex-shrink: 0;
                border-radius: 50%;
                overflow: hidden;
                background-color: ${colors.NEUTRAL_0};
                transition: opacity 0.2s ease;

                &:hover {
                  opacity: 0.8;
                }
              `}
            >
              <img
                src={player.playerImage}
                alt={player.name}
                css={css`
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                `}
              />
            </Link>

            {/* Player Info */}
            <div
              css={css`
                flex: 1;
                min-width: 0;
                display: flex;
                flex-direction: column;
              `}
            >
              <Link
                to={getPlayerLink(player.playerId)}
                css={css`
                  text-decoration: none;
                  transition: opacity 0.2s ease;

                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='bold'
                  color={colors.NEUTRAL_0}
                  style='white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin: 0;'
                >
                  {player.name}
                </Text>
              </Link>
              <Link
                to={getTeamLink(player.teamId)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  text-decoration: none;
                  transition: opacity 0.2s ease;

                  &:hover {
                    opacity: 0.8;
                  }
                  ${displayTeam ? '' : 'display: none;'}
                `}
              >
                <div
                  css={css`
                    width: 16px;
                    height: 16px;
                    flex-shrink: 0;
                    border-radius: 2px;
                    overflow: hidden;
                  `}
                >
                  <img
                    src={player.teamLogo}
                    alt={player.teamName}
                    css={css`
                      width: 100%;
                      height: 100%;
                      object-fit: contain;
                    `}
                  />
                </div>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style='white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin: 0;'
                >
                  {player.teamName}
                </Text>
              </Link>
            </div>

            {/* Stat Value */}
            <Text
              variant='heading5'
              weight='semiBold'
              color={colors.NEUTRAL_0}
              style='width: 48px; text-align: right; flex-shrink: 0; margin: 0;'
            >
              {formatStatValue(player.statValue, precision)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  )
}

export default LeaderboardCard
